import axios, { CancelTokenSource } from 'axios'
import { HospitalAdditionStatusTypes } from 'models/HospitalAdditionStatus.model'

import { Agency } from '../../models/Agency.model'
import { AgencyDTO } from '../../models/AgencyDTO.model'
import { PaginatedResponse } from '../../models/PaginatedResponse.model'
import { getApiErrorMessage } from '../../utils/apiError'
import ScrubstrApiQueryService, {
  ScrubstrApiQueryServiceGetEvent,
} from '../ScrubstrApiQueryService/ScrubstrApiQueryService'

export class AgencyService {
  private autocompleteAgenciesSource: CancelTokenSource | null = null

  /**
   * @description Search for a single agency by id or permalink
   *
   * @param id
   * @param permalink
   *
   * @returns promise for a Agency
   */
  public async searchOne({ id, permalink }: { id?: number; permalink?: string }): Promise<Agency> {
    const params = {
      id,
      permalink,
    }
    const scrubstrApiQueryRequest: ScrubstrApiQueryServiceGetEvent = {
      endpoint: `agency/search-one`,
      params,
    }

    const scrubstrApiQueryService = new ScrubstrApiQueryService()
    const agencyRaw = await scrubstrApiQueryService.get<AgencyDTO>(scrubstrApiQueryRequest)
    const agency: Agency = {
      address: agencyRaw.address,
      claimStatus: agencyRaw.claimStatus,
      createdAt: agencyRaw.createdAt,
      email: agencyRaw.email,
      facebook: agencyRaw.facebook,
      id: agencyRaw.id,
      image: agencyRaw.imageURL,
      name: agencyRaw.name,
      permalink: agencyRaw.permalink,
      phone: agencyRaw.phone,
      rating: agencyRaw.averageRating,
      ratingsSummary: agencyRaw.ratingsSummary,
      reviewCount: agencyRaw.reviewCount,
      reviews: agencyRaw.reviews,
      updatedAt: agencyRaw.updatedAt,
      website: agencyRaw.website,
    }
    return agency
  }

  /**
   * @description Add a new agency
   *
   * @param data - Agency data to add
   *
   * @returns promise for the created Agency DTO
   */
  public async add(data: {
    name: string
    address?: string
    submissionNotes?: string
    newAgencyStatus?: string
  }): Promise<AgencyDTO> {
    const scrubstrApiQueryService = new ScrubstrApiQueryService()

    try {
      const agency = await scrubstrApiQueryService.post<AgencyDTO>({
        data: {
          address: data.address,
          name: data.name,
          newAgencyStatus: data.newAgencyStatus || HospitalAdditionStatusTypes.PENDING,
          submissionNotes: data.submissionNotes,
        },
        endpoint: 'agency',
      })

      return agency
    } catch (error) {
      throw new Error(getApiErrorMessage(error, 'Sorry, there was a problem adding the agency.'))
    }
  }

  public async search({
    page,
    pageSize,
    term,
    sort,
    autocomplete,
    minRating,
  }: {
    page?: number
    pageSize?: number
    term: string
    sort: string
    autocomplete?: boolean
    minRating?: number
  }): Promise<{
    agencies: Agency[]
    totalResults: number
    totalPages: number
    currentPage: number
  }> {
    const params: {
      page?: number
      pageSize?: number
      name?: string
      orderBy?: string
      orderDirection?: string
      orderAToZTies?: string
      excludeReviews?: string
      minRating?: number
    } = {
      excludeReviews: autocomplete ? 'true' : undefined,
      minRating: minRating,
      name: term || undefined,
      page,
      pageSize,
    }

    if (sort) {
      switch (sort) {
        case 'newest':
          params.orderBy = 'latestReviewDate'
          params.orderDirection = 'DESC'
          params.orderAToZTies = 'true'
          break
        case 'mostReviewed':
          params.orderBy = 'reviewCount'
          params.orderDirection = 'DESC'
          params.orderAToZTies = 'true'
          params.minRating = minRating
          break
        case 'leastReviewed':
          params.orderBy = 'reviewCount'
          params.orderDirection = 'ASC'
          params.orderAToZTies = 'true'
          break
        case 'highRating':
          params.orderBy = 'averageRating'
          params.orderDirection = 'DESC'
          params.minRating = minRating
          params.orderAToZTies = 'true'
          break
        case 'lowRating':
          params.orderBy = 'averageRating'
          params.orderDirection = 'ASC'
          params.orderAToZTies = 'true'
          break
        case 'az':
          params.orderBy = 'name'
          params.orderDirection = 'ASC'
          break
        case 'za':
          params.orderBy = 'name'
          params.orderDirection = 'DESC'
          break
        default:
          break
      }
    }

    const scrubstrApiQueryRequest: ScrubstrApiQueryServiceGetEvent = {
      endpoint: 'agency',
      params,
    }

    if (autocomplete) {
      if (this.autocompleteAgenciesSource !== null) {
        this.autocompleteAgenciesSource.cancel('cancelToken triggered')
      }
      this.autocompleteAgenciesSource = axios.CancelToken.source()
      scrubstrApiQueryRequest.cancelToken = this.autocompleteAgenciesSource.token
    }

    const agencyResults: {
      agencies: Agency[]
      totalResults: number
      totalPages: number
      currentPage: number
    } = {
      agencies: [],
      currentPage: Number(page),
      totalPages: 0,
      totalResults: 0,
    }
    try {
      const scrubstrApiQueryService = new ScrubstrApiQueryService()
      const agenciesRaw = await scrubstrApiQueryService.get<PaginatedResponse<AgencyDTO>>(scrubstrApiQueryRequest)
      // wrap to avoid errors if the request is cancelled
      if (agenciesRaw) {
        agencyResults.agencies = agenciesRaw.data.map((agency) => ({
          address: agency.address,
          createdAt: agency.createdAt,
          email: agency.email,
          facebook: agency.facebook,
          id: agency.id,
          image: agency.imageURL,
          name: agency.name,
          permalink: agency.permalink,
          phone: agency.phone,
          rating: agency.averageRating,
          reviewCount: agency.reviewCount,
          updatedAt: agency.updatedAt,
          website: agency.website,
        }))

        agencyResults.totalResults = agenciesRaw.total
        agencyResults.totalPages = Math.ceil(agenciesRaw.total / 12)
      }
    } catch (error) {
      throw 'Sorry, there was a problem with your request.'
    }

    return agencyResults
  }
}
